<template>
  <div class="nd-page calendar-page">
    <v-card class="nd-section-card calendar-card">
      <div class="calendar-toolbar">
        <div>
          <h2>{{ weekRangeLabel }}</h2>
          <p>{{ selectedDayEvents.length }} evento(s) em {{ selectedDateLabel }} · Próxima consulta: {{ nextEventLabel }}</p>
        </div>
        <div class="calendar-toolbar__actions">
          <button
            v-for="filter in statusFilters"
            :key="filter.value"
            type="button"
            class="calendar-filter-chip"
            :class="{ 'calendar-filter-chip--active': statusFilter === filter.value }"
            @click="statusFilter = filter.value"
          >
            {{ filter.label }}
          </button>
          <v-menu offset-y>
            <template #activator="{ on, attrs }">
              <v-btn outlined color="primary" elevation="0" v-bind="attrs" v-on="on">
                Esta semana
                <v-icon right size="16">mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="$refs.calendar.prev()">
                <v-list-item-title>Semana anterior</v-list-item-title>
              </v-list-item>
              <v-list-item @click="setToday">
                <v-list-item-title>Semana atual</v-list-item-title>
              </v-list-item>
              <v-list-item @click="$refs.calendar.next()">
                <v-list-item-title>Próxima semana</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-btn color="primary" outlined elevation="0" @click="setToday">
            Hoje
          </v-btn>
          <v-btn color="primary" elevation="0" to="/consultas/novo">
            <v-icon left size="18">mdi-plus</v-icon>
            Nova consulta
          </v-btn>
        </div>
      </div>

      <div class="calendar-shell">
        <section class="calendar-board">
          <div class="calendar-board__nav">
            <v-btn icon @click="$refs.calendar.prev()">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <strong>{{ calendarTitle || weekRangeLabel }}</strong>
            <v-btn icon @click="$refs.calendar.next()">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>

          <v-calendar
            ref="calendar"
            v-model="focus"
            type="week"
            :events="filteredCalendarEvents"
            :event-color="getEventColor"
            :first-interval="6"
            :interval-count="12"
            :interval-minutes="60"
            :interval-height="74"
            :weekdays="weekdays"
            locale="pt-br"
            color="primary"
            event-overlap-mode="stack"
            @click:date="selectDate"
            @click:day="selectDate"
            @click:event="openEvent"
            @change="onCalendarChange"
          >
            <template #event="{ event }">
              <div class="calendar-event" :style="{ backgroundColor: event.softColor, borderColor: event.color }">
                <div class="calendar-event__top">
                  <strong>{{ event.procedureName }}</strong>
                  <v-icon size="14" color="#1d2622">mdi-dots-horizontal</v-icon>
                </div>
                <span>{{ event.time }} - {{ event.endTime }}</span>
                <small>{{ event.patientName }}</small>
                <em v-if="event.statusText === 'Confirmado'">
                  <v-icon size="11" color="#0f9f8e">mdi-check-circle</v-icon>
                  Confirmado
                </em>
              </div>
            </template>
          </v-calendar>
        </section>
      </div>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";

const STATUS_META = {
  2: { text: "Agendado", color: "#a64bd2" },
  10: { text: "Finalizado", color: "#0f9f8e" },
  11: { text: "Cancelado", color: "#d9534f" },
  12: { text: "Confirmado", color: "#2d7dd2" },
};

export default {
  name: "CalendarView",

  data: () => ({
    focus: new Date().toISOString().slice(0, 10),
    selectedDate: new Date().toISOString().slice(0, 10),
    statusFilter: "all",
    calendarTitle: "",
    appointments: [],
    loading: false,
    weekdays: [1, 2, 3, 4, 5, 6, 0],
    statusFilters: [
      { label: "Todos", value: "all" },
      { label: "Agendado", value: "Agendado" },
      { label: "Confirmado", value: "Confirmado" },
      { label: "Finalizado", value: "Finalizado" },
    ],
  }),

  computed: {
    calendarEvents() {
      return this.appointments
        .filter((appointment) => appointment.scheduledDate)
        .map((appointment) => {
          const time = appointment.scheduledTime || "00:00";
          const status = STATUS_META[appointment.state] || { text: "Agendado", color: "#a64bd2" };
          const endTime = this.addMinutes(time, 40);
          const patientName = appointment.patient?.name || "Paciente não informado";
          const procedureName = appointment.procedure?.name || "Sem procedimento";

          return {
            id: appointment.id,
            name: `${time} ${patientName}`,
            start: `${appointment.scheduledDate} ${time}`,
            end: `${appointment.scheduledDate} ${endTime}`,
            timed: Boolean(appointment.scheduledTime),
            color: status.color,
            softColor: this.hexToSoft(status.color),
            time,
            endTime,
            patientName,
            procedureName,
            statusText: status.text,
            date: appointment.scheduledDate,
          };
        });
    },

    filteredCalendarEvents() {
      if (this.statusFilter === "all") return this.calendarEvents;
      return this.calendarEvents.filter((event) => event.statusText === this.statusFilter);
    },

    monthEvents() {
      const month = this.focus.slice(0, 7);
      return this.calendarEvents.filter((event) => event.date && event.date.startsWith(month));
    },

    selectedDayEvents() {
      return this.filteredCalendarEvents
        .filter((event) => event.date === this.selectedDate)
        .sort((a, b) => (a.time || "").localeCompare(b.time || ""));
    },

    selectedDateLabel() {
      return this.formatDate(this.selectedDate);
    },

    nextEventLabel() {
      const today = new Date().toISOString().slice(0, 10);
      const next = this.calendarEvents
        .filter((event) => event.date >= today)
        .sort((a, b) => `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`))[0];
      return next ? `${this.formatDayMonth(next.date)} ${next.time}` : "-";
    },

    selectedDayStatusLabel() {
      if (!this.selectedDayEvents.length) return "Livre";
      const confirmed = this.selectedDayEvents.filter((event) => event.statusText === "Confirmado").length;
      if (confirmed) return `${confirmed} confirmado(s)`;
      return `${this.selectedDayEvents.length} agendado(s)`;
    },

    weekRangeLabel() {
      const start = this.startOfWeek(this.focus);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return `${this.formatDayMonth(start.toISOString().slice(0, 10))} - ${this.formatDayMonth(end.toISOString().slice(0, 10))}`;
    },
  },

  created() {
    this.loadAppointments();
  },

  methods: {
    async loadAppointments() {
      this.loading = true;
      try {
        const response = await axios.get("/appointments", { params: { start: 0, limit: 500 } });
        this.appointments = response.data.data || [];
      } catch (error) {
        console.error("Erro ao carregar calendário:", error);
        this.$dialog.notify.error("Erro ao carregar consultas do calendário.", { position: "top-right", timeout: 5000 });
      } finally {
        this.loading = false;
      }
    },

    onCalendarChange({ start }) {
      if (this.$refs.calendar) {
        this.calendarTitle = this.$refs.calendar.title;
      }
      if (start && start.date) {
        this.focus = start.date;
      }
    },

    selectDate(day) {
      const date = day.date || day;
      this.selectedDate = date;
      this.focus = date;
    },

    setToday() {
      const today = new Date().toISOString().slice(0, 10);
      this.focus = today;
      this.selectedDate = today;
    },

    openEvent({ event }) {
      this.$router.push(`/consultas/${event.id}`);
    },

    getEventColor(event) {
      return event.color || "primary";
    },

    formatDate(date) {
      if (!date) return "-";
      return new Date(`${date}T00:00:00`).toLocaleDateString("pt-BR", {
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    formatDayMonth(date) {
      return new Date(`${date}T00:00:00`).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
      });
    },

    addMinutes(time, minutes) {
      const [hours, mins] = (time || "00:00").split(":").map(Number);
      const date = new Date();
      date.setHours(hours || 0, mins || 0, 0, 0);
      date.setMinutes(date.getMinutes() + minutes);
      return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
    },

    hexToSoft(hex) {
      const clean = (hex || "#a64bd2").replace("#", "");
      const bigint = parseInt(clean, 16);
      const r = (bigint >> 16) & 255;
      const g = (bigint >> 8) & 255;
      const b = bigint & 255;
      return `rgba(${r}, ${g}, ${b}, 0.18)`;
    },

    startOfWeek(dateString) {
      const date = new Date(`${dateString}T00:00:00`);
      const day = date.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      date.setDate(date.getDate() + diff);
      return date;
    },
  },
};
</script>

<style scoped>
.calendar-page {
  gap: 0;
  min-height: calc(100vh - 92px);
}

.calendar-card {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 92px);
  padding: 0;
  overflow: hidden;
}

.calendar-toolbar {
  align-items: center;
  border-bottom: 1px solid #eef0ed;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  padding: 20px 28px 16px;
}

.calendar-toolbar h2 {
  color: #17201c;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: 0;
  line-height: 1.15;
  margin: 0;
}

.calendar-toolbar p {
  color: #7b8580;
  font-size: 14px;
  line-height: 1.35;
  margin: 8px 0 0;
}

.calendar-toolbar__actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
}

.calendar-filter-chip {
  background: #f5f6f4;
  border: 0;
  border-radius: 999px;
  color: #69736d;
  cursor: pointer;
  font-size: 13px;
  font-weight: 800;
  height: 34px;
  padding: 0 16px;
}

.calendar-filter-chip--active {
  background: #111816;
  color: #ffffff;
}

.calendar-shell {
  flex: 1;
  display: flex;
  min-height: 0;
  padding: 0;
}

.calendar-board {
  border: 0;
  border-radius: 0;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.calendar-board__nav {
  flex: 0 0 auto;
  align-items: center;
  background: #ffffff;
  border-bottom: 1px solid #e8ece8;
  display: flex;
  justify-content: space-between;
  min-height: 48px;
  padding: 0 20px;
}

::v-deep .calendar-board .v-calendar {
  flex: 1;
  min-height: 720px;
}

.calendar-board__nav strong {
  color: #17201c;
  font-size: 17px;
  font-weight: 800;
  text-transform: capitalize;
}

.calendar-event {
  border-left: 3px solid currentColor;
  border-radius: 8px;
  box-shadow: none;
  color: #17201c;
  display: block;
  height: 100%;
  overflow: hidden;
  padding: 8px 10px;
}

.calendar-event__top {
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.calendar-event__top strong,
.calendar-event span,
.calendar-event small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.calendar-event__top strong {
  font-size: 12px;
  font-weight: 800;
}

.calendar-event span {
  font-size: 12px;
  margin-top: 4px;
}

.calendar-event small {
  font-size: 11px;
  font-weight: 700;
  margin-top: 4px;
}

.calendar-event em {
  align-items: center;
  background: #ffffff;
  border-radius: 999px;
  color: #17201c;
  display: inline-flex;
  font-size: 10px;
  font-style: normal;
  font-weight: 800;
  gap: 4px;
  margin-top: 8px;
  padding: 3px 8px;
}

::v-deep .v-calendar-daily__head,
::v-deep .v-calendar-daily__intervals-head {
  border-bottom: 1px solid #e8ece8;
}

::v-deep .v-calendar-daily__day {
  border-left: 1px solid #eef0ed;
}

::v-deep .v-calendar-daily__interval {
  border-top: 1px solid #eef0ed;
}

::v-deep .v-calendar-daily__interval-text {
  color: #69736d;
  font-size: 12px;
  font-weight: 700;
  padding-right: 12px;
}

::v-deep .v-calendar-weekly__head-weekday,
::v-deep .v-calendar-daily_head-day-label,
::v-deep .v-calendar-daily_head-weekday {
  color: #7b8580;
  font-weight: 800;
}

::v-deep .v-calendar-weekly__day {
  min-height: 118px;
}

@media (max-width: 1280px) {
  .calendar-shell {
    display: block;
  }
}

@media (max-width: 780px) {
  .calendar-toolbar,
  .calendar-shell {
    padding-left: 20px;
    padding-right: 20px;
  }

  .calendar-toolbar {
    flex-direction: column;
  }

  .calendar-toolbar__actions {
    justify-content: flex-start;
    width: 100%;
  }
}
</style>
